import type { 
  User, Department, Team, Role, LeaveRequest, LeaveType, LeaveBalance, 
  ApprovalStep, Workflow, WorkflowStep, Substitution 
} from '../types/frontendTypes';
import type { 
  ApiUser, ApiDepartment, ApiTeam, ApiRole, ApiLeaveRequest, 
  ApiLeaveType, ApiLeaveBalance, ApiApprovalStep, ApiWorkflow, ApiWorkflowStep, ApiSubstitution 
} from '../types/apiTypes';

// User mappers
export const mapApiUserToUser = (apiUser: ApiUser): User => ({
  id: apiUser.id,
  email: apiUser.email,
  firstName: apiUser.first_name,
  lastName: apiUser.last_name,
  employeeId: apiUser.employee_id,
  position: apiUser.position,
  hireDate: apiUser.hire_date,
  birthDate: apiUser.birth_date,
  phone: apiUser.phone,
  isActive: apiUser.is_active,
  lastLogin: apiUser.last_login,
  createdAt: apiUser.created_at,
  updatedAt: apiUser.updated_at,
  roles: apiUser.roles?.map(mapApiRoleToRole),
  departments: apiUser.departments?.map(mapApiDepartmentToDepartment),
  teams: apiUser.teams?.map(mapApiTeamToTeam),
});

export const mapUserToApiUser = (user: User): ApiUser => ({
  id: user.id,
  email: user.email,
  first_name: user.firstName,
  last_name: user.lastName,
  employee_id: user.employeeId,
  position: user.position,
  hire_date: user.hireDate,
  birth_date: user.birthDate,
  phone: user.phone,
  is_active: user.isActive,
  last_login: user.lastLogin,
  created_at: user.createdAt,
  updated_at: user.updatedAt,
  roles: user.roles?.map(mapRoleToApiRole),
  departments: user.departments?.map(mapDepartmentToApiDepartment),
  teams: user.teams?.map(mapTeamToApiTeam),
});

// Department mappers
export const mapApiDepartmentToDepartment = (apiDepartment: ApiDepartment): Department => ({
  id: apiDepartment.id,
  name: apiDepartment.name,
  code: apiDepartment.code,
  description: apiDepartment.description,
  managerId: apiDepartment.manager_id,
  supervisorId: apiDepartment.supervisor_id,
});

export const mapDepartmentToApiDepartment = (department: Department): ApiDepartment => ({
  id: department.id,
  name: department.name,
  code: department.code,
  description: department.description,
  manager_id: department.managerId,
  supervisor_id: department.supervisorId,
});

// Team mappers
export const mapApiTeamToTeam = (apiTeam: ApiTeam): Team => ({
  id: apiTeam.id,
  name: apiTeam.name,
  description: apiTeam.description,
  departmentId: apiTeam.department_id,
  managerId: apiTeam.manager_id,
  leaderId: apiTeam.leader_id,
});

export const mapTeamToApiTeam = (team: Team): ApiTeam => ({
  id: team.id,
  name: team.name,
  description: team.description,
  department_id: team.departmentId,
  manager_id: team.managerId,
  leader_id: team.leaderId,
});

// Role mappers
export const mapApiRoleToRole = (apiRole: ApiRole): Role => ({
  id: apiRole.id,
  name: apiRole.name,
  description: apiRole.description,
  isSystem: apiRole.is_system,
});

export const mapRoleToApiRole = (role: Role): ApiRole => ({
  id: role.id,
  name: role.name,
  description: role.description,
  is_system: role.isSystem,
});

// Leave Request mappers
export const mapApiLeaveRequestToLeaveRequest = (apiLeaveRequest: ApiLeaveRequest): LeaveRequest => ({
  id: apiLeaveRequest.id,
  userId: apiLeaveRequest.user_id,
  leaveTypeId: apiLeaveRequest.leave_type_id,
  startDate: apiLeaveRequest.start_date,
  endDate: apiLeaveRequest.end_date,
  reason: apiLeaveRequest.reason,
  status: apiLeaveRequest.status,
  totalDays: apiLeaveRequest.total_days,
  workingDays: apiLeaveRequest.working_days,
  substituteId: apiLeaveRequest.substitute_id,
  emergencyContact: apiLeaveRequest.emergency_contact,
  emergencyPhone: apiLeaveRequest.emergency_phone,
  handoverNotes: apiLeaveRequest.handover_notes,
  createdAt: apiLeaveRequest.created_at,
  updatedAt: apiLeaveRequest.updated_at,
  user: apiLeaveRequest.user ? mapApiUserToUser(apiLeaveRequest.user) : undefined,
  leaveType: apiLeaveRequest.leave_type ? mapApiLeaveTypeToLeaveType(apiLeaveRequest.leave_type) : undefined,
  substitute: apiLeaveRequest.substitute ? mapApiUserToUser(apiLeaveRequest.substitute) : undefined,
  approvalSteps: apiLeaveRequest.approval_steps?.map(mapApiApprovalStepToApprovalStep),
});

export const mapLeaveRequestToApiLeaveRequest = (leaveRequest: LeaveRequest): ApiLeaveRequest => ({
  id: leaveRequest.id,
  user_id: leaveRequest.userId,
  leave_type_id: leaveRequest.leaveTypeId,
  start_date: leaveRequest.startDate,
  end_date: leaveRequest.endDate,
  reason: leaveRequest.reason,
  status: leaveRequest.status,
  total_days: leaveRequest.totalDays,
  working_days: leaveRequest.workingDays,
  substitute_id: leaveRequest.substituteId,
  emergency_contact: leaveRequest.emergencyContact,
  emergency_phone: leaveRequest.emergencyPhone,
  handover_notes: leaveRequest.handoverNotes,
  created_at: leaveRequest.createdAt,
  updated_at: leaveRequest.updatedAt,
  user: leaveRequest.user ? mapUserToApiUser(leaveRequest.user) : undefined,
  leave_type: leaveRequest.leaveType ? mapLeaveTypeToApiLeaveType(leaveRequest.leaveType) : undefined,
  substitute: leaveRequest.substitute ? mapUserToApiUser(leaveRequest.substitute) : undefined,
  approval_steps: leaveRequest.approvalSteps?.map(mapApprovalStepToApiApprovalStep),
});

// Leave Type mappers
export const mapApiLeaveTypeToLeaveType = (apiLeaveType: ApiLeaveType): LeaveType => ({
  id: apiLeaveType.id,
  name: apiLeaveType.name,
  code: apiLeaveType.code,
  description: apiLeaveType.description,
  defaultDays: apiLeaveType.default_days,
  minDays: apiLeaveType.min_days,
  maxDays: apiLeaveType.max_days,
  isActive: apiLeaveType.is_active,
  requiresApproval: apiLeaveType.requires_approval,
  requiresDocument: apiLeaveType.requires_document,
  advanceNoticeDays: apiLeaveType.advance_notice_days,
});

export const mapLeaveTypeToApiLeaveType = (leaveType: LeaveType): ApiLeaveType => ({
  id: leaveType.id,
  name: leaveType.name,
  code: leaveType.code,
  description: leaveType.description,
  default_days: leaveType.defaultDays,
  min_days: leaveType.minDays,
  max_days: leaveType.maxDays,
  is_active: leaveType.isActive,
  requires_approval: leaveType.requiresApproval,
  requires_document: leaveType.requiresDocument,
  advance_notice_days: leaveType.advanceNoticeDays,
});

// Leave Balance mappers
export const mapApiLeaveBalanceToLeaveBalance = (apiLeaveBalance: any): LeaveBalance => ({
  id: apiLeaveBalance.id,
  userId: apiLeaveBalance.user_id,
  leaveTypeId: apiLeaveBalance.leave_type_id,
  year: apiLeaveBalance.year,
  initialBalance: parseFloat(apiLeaveBalance.initial_balance || 0),
  adjustments: parseFloat(apiLeaveBalance.adjustments || 0),
  used: parseFloat(apiLeaveBalance.used || 0),
  remaining: parseFloat(apiLeaveBalance.remaining || 0),
  carriedOver: parseFloat(apiLeaveBalance.carried_over || 0),
  expiresAt: apiLeaveBalance.expires_at,
  user: apiLeaveBalance.user ? mapApiUserToUser(apiLeaveBalance.user) : undefined,
  leaveType: (apiLeaveBalance.leaveType || apiLeaveBalance.leave_type) ? 
    mapApiLeaveTypeToLeaveType(apiLeaveBalance.leaveType || apiLeaveBalance.leave_type) : 
    undefined,
});

export const mapLeaveBalanceToApiLeaveBalance = (leaveBalance: LeaveBalance): ApiLeaveBalance => ({
  id: leaveBalance.id,
  user_id: leaveBalance.userId,
  leave_type_id: leaveBalance.leaveTypeId,
  year: leaveBalance.year,
  initial_balance: leaveBalance.initialBalance,
  adjustments: leaveBalance.adjustments,
  used: leaveBalance.used,
  remaining: leaveBalance.remaining,
  carried_over: leaveBalance.carriedOver,
  expires_at: leaveBalance.expiresAt,
  user: leaveBalance.user ? mapUserToApiUser(leaveBalance.user) : undefined,
  leaveType: leaveBalance.leaveType ? mapLeaveTypeToApiLeaveType(leaveBalance.leaveType) : undefined,
});

// Approval Step mappers
export const mapApiApprovalStepToApprovalStep = (apiApprovalStep: ApiApprovalStep): ApprovalStep => ({
  id: apiApprovalStep.id,
  leaveRequestId: apiApprovalStep.leave_request_id,
  workflowStepId: apiApprovalStep.workflow_step_id,
  approverId: apiApprovalStep.approver_id,
  status: apiApprovalStep.status,
  comment: apiApprovalStep.comment,
  processedAt: apiApprovalStep.processed_at,
  stepOrder: apiApprovalStep.step_order,
  approver: apiApprovalStep.approver ? mapApiUserToUser(apiApprovalStep.approver) : undefined,
});

export const mapApprovalStepToApiApprovalStep = (approvalStep: ApprovalStep): ApiApprovalStep => ({
  id: approvalStep.id,
  leave_request_id: approvalStep.leaveRequestId,
  workflow_step_id: approvalStep.workflowStepId,
  approver_id: approvalStep.approverId,
  status: approvalStep.status,
  comment: approvalStep.comment,
  processed_at: approvalStep.processedAt,
  step_order: approvalStep.stepOrder,
  approver: approvalStep.approver ? mapUserToApiUser(approvalStep.approver) : undefined,
});

// Workflow mappers
export const mapApiWorkflowToWorkflow = (apiWorkflow: ApiWorkflow): Workflow => ({
  id: apiWorkflow.id,
  name: apiWorkflow.name,
  description: apiWorkflow.description,
  isActive: apiWorkflow.is_active,
  leaveTypeId: apiWorkflow.leave_type_id,
  steps: apiWorkflow.steps?.map(mapApiWorkflowStepToWorkflowStep),
  createdAt: apiWorkflow.created_at,
  updatedAt: apiWorkflow.updated_at,
});

export const mapWorkflowToApiWorkflow = (workflow: Workflow): ApiWorkflow => ({
  id: workflow.id,
  name: workflow.name,
  description: workflow.description,
  is_active: workflow.isActive,
  leave_type_id: workflow.leaveTypeId,
  steps: workflow.steps?.map(mapWorkflowStepToApiWorkflowStep),
  created_at: workflow.createdAt,
  updated_at: workflow.updatedAt,
});

// Workflow Step mappers
export const mapApiWorkflowStepToWorkflowStep = (apiWorkflowStep: ApiWorkflowStep): WorkflowStep => ({
  id: apiWorkflowStep.id,
  name: apiWorkflowStep.name,
  description: apiWorkflowStep.description,
  order: apiWorkflowStep.order,
  approverType: apiWorkflowStep.approver_type,
  approverId: apiWorkflowStep.approver_id,
  workflowId: apiWorkflowStep.workflow_id,
});

export const mapWorkflowStepToApiWorkflowStep = (workflowStep: WorkflowStep): ApiWorkflowStep => ({
  id: workflowStep.id,
  name: workflowStep.name,
  description: workflowStep.description,
  order: workflowStep.order,
  approver_type: workflowStep.approverType,
  approver_id: workflowStep.approverId,
  workflow_id: workflowStep.workflowId,
});

// Substitution mappers
export const mapApiSubstitutionToSubstitution = (apiSubstitution: ApiSubstitution): Substitution => ({
  id: apiSubstitution.id,
  leaveRequestId: apiSubstitution.leave_request_id,
  userId: apiSubstitution.user_id,
  substituteId: apiSubstitution.substitute_id,
  status: apiSubstitution.status,
  responseComment: apiSubstitution.response_comment,
  responseDate: apiSubstitution.response_date,
  areaOfResponsibility: apiSubstitution.area_of_responsibility,
  priority: apiSubstitution.priority,
  handoverNotes: apiSubstitution.handover_notes,
  createdAt: apiSubstitution.created_at,
  updatedAt: apiSubstitution.updated_at,
  leaveRequest: apiSubstitution.leave_request ? mapApiLeaveRequestToLeaveRequest(apiSubstitution.leave_request) : undefined,
  user: apiSubstitution.user ? mapApiUserToUser(apiSubstitution.user) : undefined,
  substitute: apiSubstitution.substitute ? mapApiUserToUser(apiSubstitution.substitute) : undefined,
});

export const mapSubstitutionToApiSubstitution = (substitution: Substitution): ApiSubstitution => ({
  id: substitution.id,
  leave_request_id: substitution.leaveRequestId,
  user_id: substitution.userId,
  substitute_id: substitution.substituteId,
  status: substitution.status,
  response_comment: substitution.responseComment,
  response_date: substitution.responseDate,
  area_of_responsibility: substitution.areaOfResponsibility,
  priority: substitution.priority,
  handover_notes: substitution.handoverNotes,
  created_at: substitution.createdAt,
  updated_at: substitution.updatedAt,
  leave_request: substitution.leaveRequest ? mapLeaveRequestToApiLeaveRequest(substitution.leaveRequest) : undefined,
  user: substitution.user ? mapUserToApiUser(substitution.user) : undefined,
  substitute: substitution.substitute ? mapUserToApiUser(substitution.substitute) : undefined,
});